const resolveSelectedOfferId = offer => {
    if (!offer || typeof offer !== 'object') {
        return null;
    }

    return offer.id || offer.offer_id || null;
};

const normalizeOffer = offer => {
    if (!offer || typeof offer !== 'object') {
        return null;
    }

    const normalizedId = resolveSelectedOfferId(offer);

    return {
        ...offer,
        ...(normalizedId ? { id: normalizedId, offer_id: normalizedId } : {}),
        manual: offer.manual === true,
        is_default: offer.is_default === true,
        is_winner: offer.is_winner === true,
    };
};

const hasSameManualIdentity = (leftOffer, rightOffer) => {
    const leftCurrencyCode = leftOffer?.currency?.code || null;
    const rightCurrencyCode = rightOffer?.currency?.code || null;

    return (
        leftOffer?.description === rightOffer?.description &&
        Number(leftOffer?.value || 0) === Number(rightOffer?.value || 0) &&
        leftCurrencyCode === rightCurrencyCode
    );
};

export const offersMatch = (leftOffer, rightOffer) => {
    if (!leftOffer || !rightOffer) {
        return false;
    }

    const leftId = resolveSelectedOfferId(leftOffer);
    const rightId = resolveSelectedOfferId(rightOffer);

    if (leftId && rightId) {
        return leftId === rightId;
    }

    if (leftOffer.manual || rightOffer.manual) {
        return hasSameManualIdentity(leftOffer, rightOffer);
    }

    return false;
};

export const isSelectedOfferId = (offers, offerId) => {
    if (!offerId || !Array.isArray(offers)) {
        return false;
    }

    return offers.some(offer => resolveSelectedOfferId(offer) === offerId);
};

export const syncOfferSelections = offers => {
    const normalizedOffers = Array.isArray(offers)
        ? offers.map(normalizeOffer).filter(Boolean)
        : [];

    const defaultOfferIndex = normalizedOffers.findIndex(
        offer => offer.is_default
    );
    let resolvedDefaultIndex = -1;

    if (defaultOfferIndex >= 0) {
        resolvedDefaultIndex = defaultOfferIndex;
    } else if (normalizedOffers.length > 0) {
        resolvedDefaultIndex = 0;
    }

    const syncedOffers = normalizedOffers.map((offer, index) => ({
        ...offer,
        is_default:
            resolvedDefaultIndex >= 0 ? index === resolvedDefaultIndex : false,
    }));

    const primaryOffer =
        resolvedDefaultIndex >= 0 ? syncedOffers[resolvedDefaultIndex] : null;

    return {
        offers: syncedOffers,
        primaryOffer,
        value:
            primaryOffer && primaryOffer.value !== undefined
                ? parseFloat(primaryOffer.value) || 0
                : null,
        currency: primaryOffer?.currency || null,
    };
};

export const findMatchingOffer = (offers, targetOffer) => {
    if (!Array.isArray(offers) || !targetOffer) {
        return null;
    }

    return offers.find(offer => offersMatch(offer, targetOffer)) || null;
};

export const selectDefaultOffer = (offers, targetOffer) => {
    const syncedOffers = syncOfferSelections(offers).offers;

    return syncOfferSelections(
        syncedOffers.map(offer => ({
            ...offer,
            is_default: offersMatch(offer, targetOffer),
        }))
    ).offers;
};

export const clearWinningOfferSelections = offers =>
    syncOfferSelections(offers).offers.map(offer => ({
        ...offer,
        is_winner: false,
    }));

export const selectWinningOffer = (
    offers,
    targetOffer,
    { setDefault = true } = {}
) => {
    const syncedOffers = syncOfferSelections(offers).offers;

    return syncOfferSelections(
        syncedOffers.map(offer => {
            const isSelected = offersMatch(offer, targetOffer);

            return {
                ...offer,
                is_winner: isSelected,
                is_default: setDefault ? isSelected : offer.is_default === true,
            };
        })
    ).offers;
};

export const resolvePreferredClosedOffer = (offers, closedOffer) => {
    const syncedOffers = syncOfferSelections(offers).offers;

    return (
        findMatchingOffer(syncedOffers, closedOffer) ||
        syncedOffers.find(offer => offer.is_winner) ||
        syncedOffers.find(offer => offer.is_default) ||
        syncedOffers[0] ||
        null
    );
};

export const buildClosedOffer = offer => {
    const normalizedOffer = normalizeOffer(offer);

    return normalizedOffer
        ? {
            ...normalizedOffer,
            is_winner: true,
        }
        : null;
};

export const buildAvailableOffers = (responseOffers, selectedOffers = []) => {
    if (!Array.isArray(responseOffers)) {
        return [];
    }

    return responseOffers
        .map(offer => ({
            id: offer.id,
            name: offer.title,
            discount_percentage: offer.value,
            currency: offer.currency,
            type: offer.type,
            created_at: offer.created_at,
            image_url: offer.image_url,
        }))
        .filter(offer => !isSelectedOfferId(selectedOffers, offer.id));
};
